package top.wcpe.allincore.ui.web.preview

/** PreviewApp 主链中尚未提交的 loader 与 bundle 联合候选。 */
internal class PreviewAppPreparedCandidate internal constructor(
    internal val bundle: PreviewBundleCandidate,
    internal val ownership: PreviewCandidateOwnership,
)

/** 把 loader 候选交给 bundle 生命周期完成两阶段切换。 */
internal class PreviewAppCandidateCoordinator(
    private val lifecycle: PreviewBundleLifecycle,
    private val buildOwnership: (PreviewCandidate, PreviewBundleIdentity) -> PreviewBundleOwnership,
) {
    private var pending: PreviewAppPreparedCandidate? = null

    fun prepare(loaded: PreviewCandidateLoadResult.Success): PreviewAppPreparedCandidate {
        supersedePending()
        return try {
            val bundle = lifecycle.allocateCandidate { identity ->
                val ownership = buildOwnership(loaded.candidate, identity)
                withCommitHook(ownership, loaded.ownership)
            }
            PreviewAppPreparedCandidate(bundle, loaded.ownership).also { pending = it }
        } catch (error: Throwable) {
            loaded.ownership.discard()
            throw error
        }
    }

    fun commit(prepared: PreviewAppPreparedCandidate) {
        check(pending === prepared) { "只能提交最新 PreviewApp 候选" }
        try {
            lifecycle.commit(prepared.bundle)
            pending = null
        } catch (error: Throwable) {
            pending = null
            // current 已是候选时 resolver ownership 必然已提交，不再伪装成可 discard。
            if (lifecycle.currentIdentity != prepared.bundle.identity) {
                prepared.ownership.discard()
            }
            throw error
        }
    }

    private fun withCommitHook(
        bundle: PreviewBundleOwnership,
        loader: PreviewCandidateOwnership,
    ): PreviewBundleOwnership = PreviewBundleOwnership(
        freezeDeliveries = bundle.freezeDeliveries,
        resumeDeliveries = bundle.resumeDeliveries,
        activate = bundle.activate,
        beforeCurrentSwitch = {
            bundle.beforeCurrentSwitch()
            loader.commit()
        },
        afterCurrentSwitch = {
            bundle.afterCurrentSwitch()
            loader.confirm()
        },
        input = bundle.input,
        modelHosts = bundle.modelHosts,
        surface = bundle.surface,
        mockSession = bundle.mockSession,
        resolverLeases = retirementResources(loader) + bundle.resolverLeases,
    )

    private fun retirementResources(loader: PreviewCandidateOwnership): List<PreviewBundleResource> {
        val runtime = loader.retirements.map { retire -> PreviewBundleResource(retire) }
        val budget = loader.budgetRetirement?.let { listOf(PreviewBundleResource(it::dispose)) }.orEmpty()
        return runtime + budget
    }

    private fun supersedePending() {
        pending?.ownership?.discard()
        pending = null
    }
}
